import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import isArray from 'lodash/isArray';
import map from 'lodash/map';
import find from 'lodash/find';
import union from 'lodash/union';
import take from 'lodash/take';
import ScrollLazyLoad from '@Components/Results/utils/scroll-lazy-load.component';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import { resultsConfig } from '@Constants/Results';
import Breadcrumb from '@Services/Breadcrumb';
import Entries from '@Services/Entries';
import ReactionTopButtons from '@Components/Results/reactions/reaction-details/ReactionTopButtons';
import ReactionDetailsTable from '@Components/Results/reactions/reaction-details/ReactionDetailsTable';
import { withToggleConditionsGlobalConsumer } from '@Pages/Results/ToggleConditionsGlobalHOCs';

const MINIMAL_VIEW_MAX_CONDITIONS_COUNT = 3;

function _defaultDetailsLimit(detailsCount, isViewPreview = false, isMinimalView = false) {
  if (isViewPreview) {
    return Math.min(detailsCount, 100);
  }
  return detailsCount <= 5 && !isMinimalView ? detailsCount : MINIMAL_VIEW_MAX_CONDITIONS_COUNT;
}

function getDetailsOpen(metadata, type, reaction) {
  const conditionsState = Breadcrumb.getResultsStateForDb(metadata.databaseId, 'reactionConditions');
  const onResultsPage = !metadata.isViewPreview && !metadata.fromDocuments && !metadata.fromRT;

  // check restoring show/hide reaction conditions only on result page
  const checkRestore = onResultsPage && conditionsState;

  // restore of conditions state for reaction with the same id
  if (checkRestore && conditionsState?.[metadata.id]) {
    return conditionsState?.[metadata.id]?.[type];
    // hide conditions if global toggle was activated and there is no manual state for reaction with this id
  }
  if (checkRestore && !conditionsState[metadata.id] && !conditionsState.show) {
    return false;
  }
  // all other cases for results/preview page or reactions in other contexts
  switch (type) {
    case 'isHitDetailsOpen':
      return !metadata.fromDocuments && !metadata.fromRT;

    case 'isAllDetailsOpen':
      return !metadata.isViewPreview && !metadata.fromDocuments && !metadata.fromRT && isEmpty(reaction?.hitDetails || []);
    default:
      return undefined;
  }
}

function shapeDetails(details, products) {
  if (!isArray(details) || !details.length) return [];
  return details.map((item) => {
    if (isArray(item.yields)) {
      return {
        ...item,
        yields: map(products, (product) => find(item.yields, { id: product.details?.id })),
      };
    }
    return item;
  });
}

function ReactionDetailsComponent(props) {
  const { metadata, reaction, spinnerId, displayAllConditions, showConditions } = props;

  // Details/hitDetails state shape may get mutated in place in showMore/showLess
  const initialDetails = React.useMemo(() => {
    if (isArray(reaction.details) && reaction.details.length) {
      const limit = _defaultDetailsLimit(reaction.detailsCount, metadata.isViewPreview, !displayAllConditions);
      if (reaction.details?.length > limit) {
        reaction.details = reaction.details?.slice(0, limit);
      }
      return shapeDetails(reaction.details, reaction.products);
    }
    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reaction.details, reaction.detailsCount, metadata.isViewPreview, displayAllConditions, reaction.products]);

  const initialHitDetails = React.useMemo(() => {
    if (reaction.hitDetails) {
      const hitsLimit = _defaultDetailsLimit(reaction.hitDetailsCount, metadata.isViewPreview, !displayAllConditions);
      const slicedHitDetails = reaction.hitDetails?.length > hitsLimit ? reaction.hitDetails.slice(0, hitsLimit) : reaction.hitDetails;
      return shapeDetails(slicedHitDetails, reaction.products);
    }
    return [];
  }, [reaction.hitDetails, reaction.hitDetailsCount, metadata.isViewPreview, displayAllConditions, reaction.products]);

  const initialConditionsNumber = useRef(initialDetails.length);
  const initialHitsNumber = useRef(initialHitDetails.length);
  const isDetailsLoading = useRef(false);

  const [details, setDetails] = useState(initialDetails);
  const [hitDetails, setHitDetails] = useState(initialHitDetails);
  const [isHitDetailsOpen, setIsHitDetailsOpen] = useState(getDetailsOpen(metadata, 'isHitDetailsOpen', reaction));
  const [isAllDetailsOpen, setIsAllDetailsOpen] = useState(getDetailsOpen(metadata, 'isAllDetailsOpen', reaction));

  useEffect(() => {
    if (metadata.expandConditions) {
      setIsAllDetailsOpen(true);
    }
  }, [metadata.expandConditions]);

  useEffect(() => {
    const conditionsState = Breadcrumb.getResultsStateForDb(metadata.databaseId, 'reactionConditions') || {};

    conditionsState[metadata.id] = {
      isAllDetailsOpen,
      isHitDetailsOpen,
    };

    if (!metadata.isViewPreview && !metadata.fromDocuments && !metadata.fromRT) {
      Breadcrumb.updateResultsStateForDb(metadata.databaseId, 'reactionConditions', conditionsState);
    }
    // handle toggling when showConditions changes
  }, [isAllDetailsOpen, isHitDetailsOpen, metadata]);

  useEffect(() => {
    // Only run on showConditions change specifically
    if (showConditions !== undefined) {
      setIsHitDetailsOpen(showConditions);
      setIsAllDetailsOpen(showConditions ? isEmpty(reaction.hitDetails) : false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showConditions]);

  const toggleHitDetailsOpen = useCallback(() => {
    setIsHitDetailsOpen((prev) => !prev);
  }, []);

  const showMoreReactionDetails = useCallback(
    (hitData, firstItem = 1, lastItem = 100) => {
      if (!isDetailsLoading.current) {
        isDetailsLoading.current = true;

        const { resultName, order, sort } = metadata;
        const { index } = reaction;
        const databaseId = reaction?.databaseId || metadata?.databaseId;

        return Entries.getReactionDetails({ index, resultName, databaseId, firstItem, lastItem, order, sort, hitData })
          .then((data) => {
            if (isArray(data)) {
              if (hitData) {
                setHitDetails((prevHitDetails) => union(prevHitDetails, data));
              } else {
                const shaped = shapeDetails(data, reaction.products);
                setDetails((prevDetails) => union(prevDetails, shaped));
              }
            }
          })
          .finally(() => {
            isDetailsLoading.current = false;
          })
          .catch((e) => {
            if (window.Logger && window.Logger.reject) {
              window.Logger.reject(e);
            }
          });
      }
      return Promise.resolve();
    },

    [metadata, reaction]
  );

  const loadInitialReactionDetails = useCallback(() => {
    let limit = Math.min(metadata.detailsCount, resultsConfig.INITIAL_ITEMS_AMOUNT.reactionDetails);

    if (isEmpty(details)) {
      // case lazyload
      if (reaction.hitDetailsCount === 0) {
        limit = _defaultDetailsLimit(reaction.detailsCount, metadata.isViewPreview, !displayAllConditions);
        initialConditionsNumber.current = limit;
      }

      return showMoreReactionDetails(false, 1, limit);
    }
    if (details.length <= limit) {
      // case no lazyload, retrieved as combinedResponse
      initialConditionsNumber.current = 0;
      initialHitsNumber.current = 0;
      return showMoreReactionDetails(false, details.length + 1, limit);
    }

    return Promise.resolve(details);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details, metadata.detailsCount, metadata.isViewPreview, displayAllConditions, reaction.hitDetailsCount]);

  useEffect(() => {
    if (isAllDetailsOpen && reaction.detailsCount > 0 && isEmpty(details) && !isDetailsLoading.current) {
      loadInitialReactionDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAllDetailsOpen, details, reaction.detailsCount]);

  const toggleAllDetailsOpen = useCallback(() => {
    if (isEmpty(details) && reaction.detailsCount > 0) {
      setIsAllDetailsOpen(true);
      loadInitialReactionDetails();
      return;
    }

    setIsAllDetailsOpen((prev) => !prev);
  }, [details, reaction.detailsCount, loadInitialReactionDetails]);

  const showLessReactionDetails = useCallback((hitData) => {
    if (hitData) {
      setHitDetails((prev) => take(prev, initialHitsNumber.current));
    } else {
      setDetails((prev) => {
        const firstN = take(prev, initialConditionsNumber.current);
        if (isEmpty(firstN)) {
          setIsAllDetailsOpen(false);
        }
        return firstN;
      });
    }
  }, []);

  const getHitDetailsTable = () =>
    !isEmpty(hitDetails) ? (
      <ReactionDetailsTable
        metadata={metadata}
        reactionId={reaction.id}
        databaseId={reaction.databaseId}
        details={hitDetails}
        detailsCount={reaction.hitDetailsCount}
        largestProduct={reaction.largestProduct}
        initialDetailsNumber={initialHitsNumber.current}
        isHitDetails
        hasHitDetails
        spinnerId={spinnerId}
        index={reaction.index}
        showMoreHandler={({ firstItem, lastItem }) => showMoreReactionDetails(true, firstItem, lastItem)}
        showLessHandler={hitDetails.length > initialHitsNumber.current ? () => showLessReactionDetails(true) : null}
        showNextConditions={
          reaction.hitDetailsCount !== reaction.detailsCount &&
          !isAllDetailsOpen &&
          reaction.detailsCount > 0 &&
          `Show ${reaction.detailsCount > 100 ? 'next 100' : 'all'} conditions`
        }
        toggleAllDetails={toggleAllDetailsOpen}
        displayFooter={displayAllConditions}
      />
    ) : null;

  const getDetailsTable = () =>
    !isEmpty(details) ? (
      <ReactionDetailsTable
        metadata={metadata}
        reactionId={reaction.id}
        databaseId={reaction.databaseId}
        details={details}
        detailsCount={reaction.detailsCount}
        largestProduct={reaction.largestProduct}
        initialDetailsNumber={initialConditionsNumber.current}
        isHitDetails={false}
        hasHitDetails={reaction.hitDetailsCount > 0}
        spinnerId={spinnerId}
        index={reaction.index}
        showMoreHandler={({ firstItem, lastItem }) => showMoreReactionDetails(false, firstItem, lastItem)}
        showLessHandler={details.length > initialConditionsNumber.current ? () => showLessReactionDetails(false) : null}
        showNextConditions={false}
        displayFooter={displayAllConditions}
      />
    ) : (
      <ScrollLazyLoad load={loadInitialReactionDetails} viewportOffset={1} container=".results-wrapper" />
    );

  return (
    <div className="react-reaction-details-wrapper">
      {displayAllConditions && (
        <ReactionTopButtons
          metadata={metadata}
          reaction={reaction}
          index={reaction.index}
          isHitDetailsOpen={isHitDetailsOpen}
          isAllDetailsOpen={isAllDetailsOpen}
          toggleHitDetailsOpen={toggleHitDetailsOpen}
          toggleAllDetailsOpen={toggleAllDetailsOpen}
          spinnerId={spinnerId}
        />
      )}
      {reaction.hitDetailsCount > 0 && isHitDetailsOpen && getHitDetailsTable()}
      {reaction.detailsCount > 0 && isAllDetailsOpen && getDetailsTable()}
    </div>
  );
}

ReactionDetailsComponent.propTypes = {
  metadata: MetadataPropTypesShape,
  reaction: PropTypes.object.isRequired,
  spinnerId: PropTypes.string.isRequired,
  databaseId: PropTypes.string,
  displayAllConditions: PropTypes.bool,
  showConditions: PropTypes.bool,
};

export default withToggleConditionsGlobalConsumer()(ReactionDetailsComponent);
